import { request } from '~/services/common';

import { expandLensDataResponse } from './leLensTransport';

type Snapshot = ReturnType<typeof expandLensDataResponse>;

export async function loadLensDataStream(
  url: string,
  options: { signal: AbortSignal; headers: Record<string, string>; body: string },
  onSnapshot: (snapshot: Snapshot) => void,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<Snapshot> {
  const controller = new AbortController();
  let timedOut = false;
  const abort = () => controller.abort();
  options.signal.addEventListener('abort', abort, { once: true });
  if (options.signal.aborted) abort();
  const timer = window.setTimeout(() => {
    timedOut = true;
    abort();
  }, timeoutMs);
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  try {
    const response = (await request(url, {
      ...options,
      method: 'POST',
      signal: controller.signal,
      returnResponse: true,
    })) as Response;
    if (!response.headers.get('content-type')?.includes('application/x-ndjson')) {
      return expandLensDataResponse(await response.json());
    }
    reader = response.body?.getReader();
    if (!reader) throw new Error('No Lens data stream was returned. Please retry.');
    const decoder = new TextDecoder();
    let buffered = '';
    let partial: Snapshot | undefined;
    let complete: Snapshot | undefined;
    const readLine = (line: string) => {
      if (!line.trim()) return;
      const event = JSON.parse(line);
      if (event.type === 'error') throw new Error(event.message);
      if (event.type === 'complete') {
        complete = expandLensDataResponse(event);
      } else if (event.type === 'card') {
        const snapshot = expandLensDataResponse(event);
        partial = { ...snapshot, cards: [...(partial?.cards ?? []), ...snapshot.cards] };
        if (!controller.signal.aborted) onSnapshot(partial);
      }
    };
    while (!controller.signal.aborted) {
      const { done, value } = await reader.read();
      buffered += decoder.decode(value, { stream: !done });
      const lines = buffered.split('\n');
      buffered = lines.pop() ?? '';
      lines.forEach(readLine);
      if (done) {
        readLine(buffered);
        break;
      }
    }
    if (controller.signal.aborted) throw new Error('Lens data loading was cancelled.');
    if (!complete) throw new Error('The Lens data stream ended before all results arrived. Please retry.');
    return complete;
  } catch (error) {
    if (timedOut) throw new Error(timeoutMessage, { cause: error });
    throw error;
  } finally {
    window.clearTimeout(timer);
    options.signal.removeEventListener('abort', abort);
    await reader?.cancel().catch(() => undefined);
  }
}
